import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR
} from "@angular/forms";
import { Subscription } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";

@Component({
  selector: "kwikui-input-otp",
  templateUrl: "./input-otp.component.html",
  styleUrls: ["./input-otp.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KwikUIInputOtpComponent),
      multi: true
    }
  ]
})
export class KwikUIInputOtpComponent
  implements OnInit, OnChanges, ControlValueAccessor {
  @Input() disabled = false;

  @Input() focus = false;

  @Input() formControl: FormControl = new FormControl({});

  @Input() formControlName = "form_control_name";

  @Input() id = "";

  @Input() invalid = false;

  @Input() label = "";

  @Input() mask = "# # # #";

  @Input() mode: "single" | "multiple" = "single";

  @Input() otpLength = 4;

  @Input() placeholder = "";

  @Input() postfix = "";

  @Input() prefix = "";

  @Input() properties = { readOnly: false, hidePlaceholder: false };

  @Input() size: "s" | "m" | "l" = "m";

  @Input() validators = { required: true };

  @Output() getKeyValue: EventEmitter<any> = new EventEmitter<any>();

  formGroup: FormGroup = new FormGroup({});

  otpMultipleConfig = {
    allowKeyCodes: [],
    allowNumbersOnly: true,
    containerClass: "",
    containerStyles: {},
    disableAutoFocus: true,
    inputClass: "",
    inputStyles: {},
    isPasswordInput: false,
    length: this.otpLength,
    placeholder: "",
    size: this.size
  };

  subscriptions = new Subscription();

  constructor() {}

  ngOnInit(): void {
    this.formGroup.addControl(this.formControlName, this.formControl);
    this.focus = this.focus === undefined ? false : this.focus;
    this.invalid =
      this.invalid === undefined ? false : this.invalid && this.focus;
    this.validators = { ...this.validators };
    this.properties = { ...this.properties };
    this.mode =
      this.mode !== "single" && this.mode !== "multiple"
        ? "multiple"
        : this.mode;
    this.otpMultipleConfig = {
      ...this.otpMultipleConfig,
      length: this.otpLength,
      placeholder: this.properties.hidePlaceholder
        ? ""
        : this.placeholder.length > 0
          ? "0"
          : this.placeholder,
      size: this.size
    };
    this.setDisabled();

    this.subscriptions.add(
      this.formGroup.controls[this.formControlName].valueChanges
        .pipe(distinctUntilChanged()) // makes sure the value has actually changed.
        .subscribe((value) => this.handleInputValueChange(value))
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.hasOwnProperty("formControl") &&
      !changes.formControl.firstChange
    ) {
      this.formControl = changes.formControl.currentValue;
    }
    if (changes.hasOwnProperty("invalid") && !changes.invalid.firstChange) {
      this.invalid = Boolean(changes.invalid.currentValue);
      this.setError();
    }
    if (changes.hasOwnProperty("focus") && !changes.focus.firstChange) {
      this.focus = Boolean(changes.focus.currentValue);
    }
    if (changes.hasOwnProperty("focus") && !changes.focus.firstChange) {
      this.focus = Boolean(changes.focus.currentValue);
    }
    if (changes.hasOwnProperty("size") && !changes.size.firstChange) {
      this.size = changes.size.currentValue;
    }
    if (changes.hasOwnProperty("disabled") && !changes.disabled.firstChange) {
      this.disabled = Boolean(changes.disabled.currentValue);
      this.setDisabled();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // ensure when component is destroyed the subscription is also and not left open.
  }

  /**
   * @description Handles updating the config object with the latest values for multiple mode
   */
  setConfigProperties() {
    this.otpMultipleConfig = {
      ...this.otpMultipleConfig,
      length: this.otpLength,
      placeholder: this.placeholder.length > 0 ? "0" : this.placeholder,
      size: this.size
    };
  }

  /**
   * @description Handles setting up of error and focus on the input field is it is invalid
   */
  setDisabled() {
    if (this.formGroup.controls[this.formControlName] !== undefined) {
      if (this.disabled === true) {
        this.formGroup.controls[this.formControlName].disable({
          emitEvent: false
        });
      } else {
        this.formGroup.controls[this.formControlName].enable({
          emitEvent: false
        });
      }
    }
  }

  /**
   * @description Handles setting up of error and focus on the input field is it is invalid
   */
  setError() {
    if (
      this.formGroup.controls[this.formControlName] !== undefined &&
      this.formGroup.controls[this.formControlName].invalid
    ) {
      this.invalid = true;
      this.focus = true;
    } else {
      this.invalid = false;
      this.focus = false;
    }
  }

  /**
   * @description Handles firing of 2 events on (keyup) event
   *
   * @param value
   */
  handleInputValueChange(value: any) {
    if (value === this.formControl.value && this.mode === "single") {
      this.emitEvent(this.getKeyValue, {
        key: this.formControlName,
        value: this.formControl.value
      });
    }
  }

  handleInputValueChangeMultiple(value: any) {
    if (this.mode === "multiple") {
      this.formControl.patchValue(value);
      this.formGroup.controls[this.formControlName].patchValue(value);
      this.emitEvent(this.getKeyValue, {
        key: this.formControlName,
        value: this.formControl.value
      });
    }
  }

  handleFocusedChange(e: any) {
    if (!this.invalid) {
      this.focus = e;
    }
  }

  emitEvent(event: any, data: any) {
    event.emit(data);
  }

  /** Method Implementations for Abstract Control */
  writeValue(value: any): void {}

  registerOnChange(fn: any): void {}

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}
}
